/**
 * How a scan maps to the floor plan view (the assembler) and back, shared by
 * every host: which groups are rooms, the room each draws as, and how
 * assembled rooms land on the scan.
 */
import { defaultShape, type FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import { getEffectiveScanValueAsNumber } from '../ScanRoot';
import { getScanWithUpdatedGroup, type ScanGroupOptimized, type ScanRootOptimized } from '../ScanRootOptimized';
import { getGroupUpdatesFromFloorPlanRoom } from '../StandardScanTemplateRoot/flooringLib';
import { getGroupsInStandardGroupOrder } from '../StandardScanTemplateRoot/StandardScanPresentation';

/** Whether the group's template measures a room (exposes `roomArea`). */
export const groupHasRoomAreaTemplate = (group?: ScanGroupOptimized): group is ScanGroupOptimized => !!group?.template.values?.roomArea;

/**
 * The group's room, new-system-first: its drawn floor plan, else a plain
 * rectangle synthesized from the legacy roomWidth, roomLength and roomHeight
 * values so groups predating the floor plan model still draw. Undefined when
 * neither source has both a width and a length.
 */
export const getGroupFloorPlanRoom = (group?: ScanGroupOptimized): FloorPlanRoom | undefined => {
  if (group?.roomFloorPlan) return group.roomFloorPlan;
  const width = getEffectiveScanValueAsNumber(group?.values?.roomWidth);
  const length = getEffectiveScanValueAsNumber(group?.values?.roomLength);
  if (width == null || length == null) return undefined;
  return { width, length, height: getEffectiveScanValueAsNumber(group?.values?.roomHeight) };
};

/**
 * The room with a ceiling to draw with. A room with no ceiling height draws as
 * if every wall were an opening (height-0 walls draw dashed) with zero wall
 * area, never what a real room means, so it gets the default ceiling.
 */
export const withDefaultCeiling = (room: FloorPlanRoom): FloorPlanRoom => room.height == null ? { ...room, height: defaultShape().height } : room;

/** The scan's groups that have a room (drawn, or a rectangle from legacy dimensions), in the standard group order. */
export const getAssemblyGroups = (scanRoot: ScanRootOptimized): ScanGroupOptimized[] => {
  const { clusterOrder, groupClusters } = getGroupsInStandardGroupOrder(
    scanRoot.template,
    scanRoot.groups?.filter(group => getGroupFloorPlanRoom(group) != null),
  );
  return clusterOrder.flatMap(category => groupClusters[category] ?? []);
};

/**
 * The room as its group saves it: the assembler never changes a ceiling
 * height, so a group without one gets none back (and its wall area stays
 * zero) rather than the default ceiling the room was drawn with.
 */
const withSavedCeiling = (room: FloorPlanRoom, group: ScanGroupOptimized): FloorPlanRoom => {
  if (getGroupFloorPlanRoom(group)?.height != null) return room;
  const { height: _drawnCeiling, ...heightless } = room;
  return heightless;
};

/**
 * One pure scan update for every changed room: each room lands on its group
 * exactly as a single-room save does (`getGroupUpdatesFromFloorPlanRoom`,
 * derived values included), folded into one new scan. Ids with no group are
 * ignored.
 */
export const getScanWithAssembledRooms = (scan: ScanRootOptimized, changedRooms: readonly { id: string; room: FloorPlanRoom }[]): ScanRootOptimized =>
  changedRooms.reduce((current, { id, room }) => {
    const group = current.groupsById[id];
    return group ? getScanWithUpdatedGroup(current, id, getGroupUpdatesFromFloorPlanRoom(group, withSavedCeiling(room, group))) : current;
  }, scan);
